// Resolves a document key in the media bucket to its public URL, the way
// imageUrl.ts does for images. Documents are served by their own CloudFront
// (stack biet-docs-cdn) reading the bucket directly — the image CDN puts a
// transform in the path and answers a PDF with `400 RequestTypeError`.
//
// There is no size/format ladder here: a document is served as uploaded, so
// the key maps straight onto a URL with nothing in between.
export const DOC_CDN_BASE: string =
  (import.meta.env?.VITE_DOC_CDN_BASE as string | undefined) ?? "";

/** A published document: a media-bucket key plus what a reader needs to see. */
export interface Doc {
  /** Media-bucket key, e.g. "sites/biet/docs/…". Resolve with docUrl(). */
  url: string;
  name: string;
  /** Real size of the published file, measured at ingest. */
  bytes: number;
  /** Where the institute publishes it, kept so it can be re-checked later. */
  source?: string;
}

/**
 * Public URL for a document key (e.g. "sites/biet/docs/library-user-manual.pdf").
 *
 * Absolute URLs pass through untouched, so a link that still points at a
 * third-party host keeps working while it is migrated. When no CDN base is
 * configured the key is returned unchanged, matching imageUrl's behaviour.
 */
export function docUrl(key: string, base: string = DOC_CDN_BASE): string {
  if (key.includes("://")) return key;
  if (!base) return key;
  return `${base.replace(/\/$/, "")}/${key.replace(/^\//, "")}`;
}

/** Human-readable size for a byte count, e.g. 483327 -> "472 KB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
